import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, CheckCircle2, Dna, FlaskConical, Clock, Loader2 } from "lucide-react";

const TEST_VIDEOS = {
  preventest: "https://player.vimeo.com/video/1207441221?h=23101f4a61",
};

export default function TestDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setTest(null);
    api.get(`/tests/${slug}`).then(({ data }) => setTest(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-32 text-center">
          <h1 className="font-display text-3xl text-navy">Test not found</h1>
          <Link to="/#tests" className="mt-6 inline-block text-patriot font-semibold">Back to tests</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-navy" />
      </div>
    );
  }

  const meta = [
    { icon: Dna, label: "Genes / Scope", value: test.gene_count },
    { icon: FlaskConical, label: "Sample Type", value: test.sample_type },
    { icon: Clock, label: "Turnaround", value: test.turnaround },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="bg-navy-deep text-white afha-grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <button onClick={() => navigate("/#tests")} data-testid="back-to-tests" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" /> All GeneID Tests
          </button>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className={TEST_VIDEOS[slug] ? "lg:col-span-6" : "lg:col-span-12"}>
              <span className="text-xs font-bold tracking-wide uppercase text-patriot-light">{test.category}</span>
              <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-3">{test.name}</h1>
              <p className="mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">{test.short}</p>
            </div>
            {TEST_VIDEOS[slug] && (
              <div className="lg:col-span-6" data-testid="test-header-video">
                <div className="relative w-full rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10" style={{ paddingTop: "56.25%" }}>
                  <iframe
                    title={`${test.name} video`}
                    src={TEST_VIDEOS[slug]}
                    className="absolute inset-0 h-full w-full"
                    frameBorder="0"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <h2 className="font-display text-2xl text-navy mb-4">Overview</h2>
          <p className="text-muted-foreground leading-relaxed">{test.long}</p>

          <h3 className="font-display text-xl text-navy mt-10 mb-5">What it offers</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {(test.highlights || []).map((h) => (
              <div key={h} className="flex items-start gap-2.5 bg-white border border-border rounded-lg p-4">
                <CheckCircle2 className="h-5 w-5 text-patriot shrink-0 mt-0.5" />
                <span className="text-sm text-navy">{h}</span>
              </div>
            ))}
          </div>

          <h3 className="font-display text-xl text-navy mt-10 mb-5">Who it's for</h3>
          <ul className="space-y-2">
            {(test.who || []).map((w) => (
              <li key={w} className="flex items-center gap-3 text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-navy" /> {w}
              </li>
            ))}
          </ul>
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-white border border-border rounded-xl p-6 sticky top-24">
            <h3 className="font-display text-lg text-navy mb-5">Test Details</h3>
            <div className="space-y-4">
              {meta.map((m) => (
                <div key={m.label} className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-navy/5 flex items-center justify-center shrink-0">
                    <m.icon className="h-5 w-5 text-navy" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{m.label}</p>
                    <p className="text-sm font-semibold text-navy">{m.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-4">Want to offer {test.name} through your network?</p>
              <Button onClick={() => navigate("/signup")} data-testid="test-join-btn" className="w-full rounded-full bg-patriot hover:bg-patriot-dark text-white gap-2">
                Become a Rep <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </aside>
      </section>

      <Footer />
    </div>
  );
}
